import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  rectIntersection,
  DragOverlay,
  Active,
  ClientRect,
  DroppableContainer,
  DragMoveEvent,
  TouchSensor,
} from '@dnd-kit/core';
import { RectMap } from '@dnd-kit/core/dist/store';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Coordinates } from '@dnd-kit/utilities';
import { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';

import {
  useCurrentSubscriptionQuery,
  useUpdateUserProfileFieldsMutation,
  useUserProfileFieldsQuery,
} from 'api';
import { useDeleteUserProfileMutation } from 'api/mutations/useDeleteUserProfileMutation';
import { ReactComponent as AddIcon } from 'assets/icons/addIcon.svg';
import { alert, GoBackButtonWithTitle, Spinner } from 'components';
import { AddFieldDialog } from 'components/Dialogs/AddFieldDialog';
import { CreateButton } from 'features/documentCategories/components/DocumentCategories/styled';
import { useAuth, useModal, useWindowSize } from 'hooks';
import { theme } from 'styles';
import { UserProfileField } from 'types';

import SortableField from './SortableField';
import {
  ActiveFieldW,
  ContentW,
  InnerTitleW,
  InnerW,
  PageSubTitle,
  PageTitle,
  PageW,
  PublicInfoContainer,
  SpinnerW,
} from './styled';

// Custom collision detection to prioritize droppable areas
const customCollisionDetection = (args: {
  active: Active;
  collisionRect: ClientRect;
  droppableRects: RectMap;
  droppableContainers: DroppableContainer[];
  pointerCoordinates: Coordinates | null;
}) => {
  const droppableCollisions = rectIntersection(args);
  if (droppableCollisions.length > 0) {
    return droppableCollisions;
  }
  return closestCenter(args);
};

export function UserProfileManagement(): ReactElement {
  useCurrentSubscriptionQuery();
  const { isManager } = useAuth();
  const { t } = useTranslation();
  const { isMobile } = useWindowSize();
  const { handleOpenModal, handleCloseModal, isOpen } = useModal();
  const { isLoading, userProfileFields = [] } = useUserProfileFieldsQuery({ management: true });

  const { editUserProfileFieldsMutation } = useUpdateUserProfileFieldsMutation({
    invalidateQueries: ['user-profile-fields'],
  });

  const { deleteUserProfileFieldMutation } = useDeleteUserProfileMutation();

  const [localFields, setLocalFields] = useState<UserProfileField[]>(userProfileFields);
  const [hoveredZone, setHoveredZone] = useState<'public' | 'private' | null>(null);
  const [activeField, setActiveField] = useState<UserProfileField | null>(null);
  const [category, setCategory] = useState('');

  useEffect(() => {
    setLocalFields(userProfileFields);
  }, [userProfileFields]);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = (event: DragMoveEvent) => {
    const { active } = event;
    const draggedField = localFields.find((field) => field.id === active.id);
    setActiveField(draggedField || null);
    setHoveredZone(null);
  };

  const handleDragMove = (event: DragMoveEvent) => {
    const { over } = event;
    if (!over) {
      if (hoveredZone !== null) {
        setHoveredZone(null);
      }
      return;
    }

    const overDroppable = over.data.current?.type === 'droppable';
    if (overDroppable) {
      const targetInfo = over.data.current?.category;
      const newHoveredZone = targetInfo === 'public' ? 'public' : 'private';
      if (hoveredZone !== newHoveredZone) {
        setHoveredZone(newHoveredZone);
      }
    } else if (hoveredZone !== null) {
      setHoveredZone(null);
    }
  };

  const handleDragEnd = async (event: DragMoveEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      setHoveredZone(null);
      setActiveField(null);
      return;
    }

    const activeId = active.id;
    const overId = over.id;

    try {
      const updatedFields = localFields.map((f) => ({ ...f }));
      const activeField = updatedFields.find((item) => item.id === activeId);
      const overField = updatedFields.find((item) => item.id === overId);

      if (!activeField || !overField) return;

      const overDroppable = over.data.current?.type === 'droppable';
      if (overDroppable) {
        activeField.category = over.data.current?.category;
      } else {
        activeField.category = overField.category;
      }

      const sortedFields = updatedFields.sort((a, b) => a.orders - b.orders);
      const activeIndex = sortedFields.findIndex((item) => item.id === activeId);
      const overIndex = sortedFields.findIndex((item) => item.id === overId);

      const [movedField] = sortedFields.splice(activeIndex, 1);
      sortedFields.splice(overIndex, 0, movedField);

      const result = sortedFields.map((field, index) => ({ ...field, orders: index }));

      setLocalFields(result);

      await editUserProfileFieldsMutation(result);
      alert.success(t('toasts.fields.success.edit'));
    } catch (error) {
      setLocalFields(userProfileFields);
      alert.error(t('toasts.codes.fieldSaveFailed'));
    }

    setHoveredZone(null);
    setActiveField(null);
  };

  const addField = async (label: string) => {
    if (!label) {
      alert.error(t('toasts.codes.fieldEmpty'));
      return;
    }

    const isDuplicate = localFields.some((field) => field.label === label);

    if (isDuplicate) {
      alert.error(t('toasts.codes.duplicateLabel'));
      return;
    }

    const newField = {
      id: uuid(),
      label,
      category,
      hidden: false,
      orders: localFields.length,
      defaults: false,
      data_count: 0,
    };

    try {
      await editUserProfileFieldsMutation([newField]);
      alert.success(t('toasts.fields.success.create'));
      handleCloseModal();
    } catch (error) {
      alert.error(t('toasts.codes.fieldSaveFailed'));
    }
  };

  const handleFieldUpdate = async (id: string, newLabel: string) => {
    if (!newLabel) {
      alert.error(t('toasts.codes.fieldEmpty'));
      return;
    }

    const isDuplicate = userProfileFields.some(
      (field) => field.id !== id && field.label === newLabel,
    );

    if (isDuplicate) {
      alert.error(t('toasts.codes.duplicateLabel'));
      return;
    }

    const updatedFields = userProfileFields.map((field) =>
      field.id === id ? { ...field, label: newLabel } : field,
    );

    try {
      await editUserProfileFieldsMutation(updatedFields);
      alert.success(t('toasts.fields.success.edit'));
    } catch (error) {
      alert.error(t('toasts.codes.fieldSaveFailed'));
    }
  };

  const handleHideToggle = async (id: string, hidden: boolean) => {
    const updatedFields = userProfileFields.map((field) =>
      field.id === id ? { ...field, hidden } : field,
    );

    try {
      await editUserProfileFieldsMutation(updatedFields);
      alert.success(t('toasts.fields.success.edit'));
    } catch (error) {
      alert.error(t('toasts.codes.fieldSaveFailed'));
    }
  };

  const handleOnFieldDelete = async (id: string) => {
    try {
      await deleteUserProfileFieldMutation({ fieldId: id });
    } catch (error) {
      alert.error(t('toasts.codes.fieldDeleteFailed'));
    }
  };

  const sortedPublicFields = localFields
    .filter((field: UserProfileField) => field.category === 'public')
    .sort((a, b) => a.orders - b.orders);

  const sortedPrivateFields = localFields
    .filter((field: UserProfileField) => field.category === 'private')
    .sort((a, b) => a.orders - b.orders);

  const { setNodeRef: setPublicDroppableRef } = useDroppable({
    id: 'public-droppable',
    data: { type: 'droppable', category: 'public' },
  });

  const { setNodeRef: setPrivateDroppableRef } = useDroppable({
    id: 'private-droppable',
    data: { type: 'droppable', category: 'private' },
  });

  if (isLoading) {
    return (
      <SpinnerW>
        <Spinner size="1.5rem" wrapperSx={{ height: '24px' }} />
      </SpinnerW>
    );
  }

  const handleAddNewPublicField = () => {
    setCategory('public');
    handleOpenModal();
  };

  const handleAddNewPivateField = () => {
    setCategory('private');
    handleOpenModal();
  };

  return (
    <PageW>
      <GoBackButtonWithTitle
        title={t('common.back')}
        containerSx={{
          marginTop: 0,
          maxWidth: '100%',
        }}
      />
      <PageTitle>{t('settings.userProfile.title')}</PageTitle>
      <ContentW>
        <DndContext
          sensors={sensors}
          collisionDetection={customCollisionDetection}
          onDragStart={handleDragStart}
          onDragMove={handleDragMove}
          onDragEnd={handleDragEnd}
        >
          <InnerW isMobile={isMobile}>
            <PublicInfoContainer ref={setPublicDroppableRef}>
              <InnerTitleW>
                <PageSubTitle>{t('settings.userProfile.publicInfo')}</PageSubTitle>
                {isManager && (
                  <CreateButton
                    startIcon={<AddIcon style={{ fill: theme.palette.common.white }} />}
                    onClick={handleAddNewPublicField}
                    style={{
                      height: '56px',
                      padding: '16px',
                      backgroundColor: theme.palette.info.main,
                      color: theme.palette.common.white,
                      fontSize: '14px',
                      fontWeight: 400,
                      borderRadius: '8px',
                      textTransform: 'none',
                    }}
                  >
                    {t('common.add')}
                  </CreateButton>
                )}
              </InnerTitleW>
              <SortableContext
                id="public"
                items={sortedPublicFields}
                strategy={verticalListSortingStrategy}
              >
                {sortedPublicFields.map((field: UserProfileField) => (
                  <SortableField
                    key={field.id}
                    id={field.id}
                    label={field.label}
                    dataCount={field.data_count}
                    hidden={field.hidden}
                    defaults={field.defaults}
                    onLabelChange={handleFieldUpdate}
                    onHideToggle={handleHideToggle}
                    onDelete={handleOnFieldDelete}
                  />
                ))}
              </SortableContext>
            </PublicInfoContainer>

            <PublicInfoContainer ref={setPrivateDroppableRef}>
              <InnerTitleW>
                <PageSubTitle>{t('settings.userProfile.privateInfo')}</PageSubTitle>
                {isManager && (
                  <CreateButton
                    startIcon={<AddIcon style={{ fill: theme.palette.common.white }} />}
                    onClick={handleAddNewPivateField}
                    style={{
                      height: '56px',
                      padding: '16px',
                      backgroundColor: theme.palette.info.main,
                      color: theme.palette.common.white,
                      fontSize: '14px',
                      fontWeight: 400,
                      borderRadius: '8px',
                      textTransform: 'none',
                    }}
                  >
                    {t('common.add')}
                  </CreateButton>
                )}
              </InnerTitleW>
              <SortableContext
                id="private"
                items={sortedPrivateFields}
                strategy={verticalListSortingStrategy}
              >
                {sortedPrivateFields.map((field: UserProfileField) => (
                  <SortableField
                    key={field.id}
                    id={field.id}
                    label={field.label}
                    dataCount={field.data_count}
                    hidden={field.hidden}
                    defaults={field.defaults}
                    onHideToggle={handleHideToggle}
                    onLabelChange={handleFieldUpdate}
                    onDelete={handleOnFieldDelete}
                  />
                ))}
              </SortableContext>
            </PublicInfoContainer>
          </InnerW>

          <DragOverlay>
            {activeField ? (
              <ActiveFieldW>
                <SortableField
                  id={activeField.id}
                  label={activeField.label}
                  dataCount={activeField.data_count}
                  defaults={activeField.defaults}
                  hidden={activeField.hidden}
                  onHideToggle={handleHideToggle}
                  onLabelChange={handleFieldUpdate}
                  onDelete={handleOnFieldDelete}
                />
              </ActiveFieldW>
            ) : null}
          </DragOverlay>
        </DndContext>
        {isOpen && (
          <AddFieldDialog
            title={t('settings.userProfile.addFieldModal.title')}
            subTitle={t('settings.userProfile.addFieldModal.title')}
            open={isOpen}
            onSubmit={addField}
            onClose={handleCloseModal}
            fieldCount={userProfileFields?.length || 0}
          />
        )}
      </ContentW>
    </PageW>
  );
}
